import { PREV_COLUMN_SUFFIX, type TileResult } from "@csbi/contracts"
import { deltaDirection, deltaIsGood, type MetricDefinition } from "@csbi/metric-catalog"
import type { ThemeMode } from "@csbi/viz-core"
import { KpiCard, type KpiDelta } from "./kpi-card.js"

export type StatWidgetProps = {
  result: TileResult
  definitions: ReadonlyMap<string, MetricDefinition>
  mode: ThemeMode
  label?: string
  hero?: boolean
  trendPoints?: number
}

export function statFromResult(result: TileResult, trendPoints = 12) {
  const measure = result.columns.find((c) => c.role === "measure")
  const timeCol = result.columns.find((c) => c.role === "time")
  if (!measure) return null

  const num = (raw: unknown): number | null =>
    typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() !== "" ? Number(raw) : null

  const rows = timeCol
    ? [...result.rows].sort((a, b) => String(a[timeCol.key] ?? "").localeCompare(String(b[timeCol.key] ?? "")))
    : result.rows

  const series = rows.map((r) => num(r[measure.key]))
  const lastIndex = series.findLastIndex((v) => v != null)
  const value = lastIndex >= 0 ? series[lastIndex]! : null

  const prevRaw = lastIndex >= 0 ? num(rows[lastIndex]?.[`${measure.key}${PREV_COLUMN_SUFFIX}`]) : null

  return {
    metricId: measure.metricId ?? measure.key,
    unit: measure.unit ?? "count",
    title: measure.title,
    value,
    previous: prevRaw,
    trend: series.slice(-trendPoints),
  }
}

export function StatWidget({ result, definitions, mode, label, hero, trendPoints = 12 }: StatWidgetProps) {
  const stat = statFromResult(result, trendPoints)
  if (!stat) return null

  const def = definitions.get(stat.metricId)
  const meta = result.meta.metrics.find((m) => m.id === stat.metricId)

  let delta: KpiDelta | undefined
  if (def && stat.previous != null && stat.previous !== 0 && stat.value != null && deltaDirection(def) !== "neutral") {
    const ratio = (stat.value - stat.previous) / Math.abs(stat.previous)
    delta = {
      ratio,
      comparedTo: "previous period",
      good: deltaIsGood(def, stat.value - stat.previous),
    }
  }

  return (
    <KpiCard
      label={label ?? def?.name ?? stat.title}
      value={stat.value}
      valueType={stat.unit}
      mode={mode}
      {...(hero ? { hero } : {})}
      {...(delta ? { delta } : {})}
      trend={stat.trend}
      {...(meta?.approximate ? { caveat: meta.caveat ?? "This value is an approximation." } : {})}
    />
  )
}
